import React, { useState, useContext } from "react"
import { useStaticQuery, graphql } from "gatsby"
import { UIContext } from "./layout"
import ReCAPTCHA from "react-google-recaptcha"
import { useForm } from "react-hook-form"
import useSiteMetadata from "./hooks/useSiteMetadata"
import Grid from "./grid"
import externalLinks from "../../external-links"
import FormInput from "./partner-form/form-input"

import {
  FormGrid,
  FormSubmitButton,
  ValidationContainer,
  ErrorBlock,
  SuccessBlock,
  FormContainer,
} from "./contact.form.style"

const HUBSPOT_ENDPOINT = externalLinks.hubspot.contactFormSubmissionApi

const ContactForm = () => {
  const query = useStaticQuery(graphql`
    query {
      successIcon: file(
        relativePath: { eq: "0_Global_Assets/icon_success.svg" }
      ) {
        publicURL
      }
      failIcon: file(relativePath: { eq: "0_Global_Assets/icon_failure.svg" }) {
        publicURL
      }
    }
  `)
  const {
    state: { location, clientLoaded },
  } = useContext(UIContext)
  const [isValidated, setIsValidated] = useState(false)
  const [hasSubmitted, setHasSubmitted] = useState(false)

  const [submissionFailure, setSubmissionFailure] = useState(false)
  const metadata = useSiteMetadata()

  const { register, errors, handleSubmit } = useForm({
    defaultValues: {
      primary_vertical: "DEFAULT",
    },
  })

  const onSubmitCallback = (data, e) => {
    e.preventDefault()

    const submissionData = Object.keys(data).reduce((acc, key) => {
      acc.push({
        name: key,
        value: data[key],
      })
      return acc
    }, [])

    const pageUri = metadata.siteURL + location.pathname

    const submission = {
      fields: [...submissionData],
      context: {
        pageUri,
        pageName: document.title,
      },
    }

    try {
      let hutk = document.cookie
        .split("; ")
        .find(row => row.startsWith("hubspotutk"))
        .split("=")[1]

      submission.context = {
        ...submission.context,
        hutk,
      }
    } catch (error) {}

    fetch(HUBSPOT_ENDPOINT, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(submission),
    })
      .then(response => response.json())
      .then(data => {
        if (!data.errors) {
          setSubmissionFailure(false)
          setHasSubmitted(true)
        } else {
          setSubmissionFailure(true)
        }
      })
      .catch(error => {
        setSubmissionFailure(true)
      })
  }

  const [showCaptcha, setShowCaptcha] = useState(false)
  React.useEffect(() => {
    if (clientLoaded && !showCaptcha) {
      setTimeout(() => {
        setShowCaptcha(true)
      }, 10000)
    }
  }, [clientLoaded, showCaptcha])

  return (
    <>
      {hasSubmitted && (
        <SuccessBlock>
          <img
            alt="Form Submission Success Icon"
            src={query.successIcon.publicURL}
          />
          <p>Thank you! Your message has been successfully sent.</p>
        </SuccessBlock>
      )}
      {Object.keys(errors).length > 0 && !hasSubmitted && (
        <ErrorBlock>
          <img
            alt="Form Submission Failure Icon"
            src={query.failIcon.publicURL}
          />
          <p>One or more fields are missing required information.</p>
        </ErrorBlock>
      )}
      {submissionFailure && (
        <ErrorBlock>
          <p>
            An issue occurred sending the message, please get in touch with us
            directly at{" "}
            <a href="mailto:contact@mappedin.com">contact@mappedin.com</a>
          </p>
        </ErrorBlock>
      )}
      <FormContainer onSubmit={handleSubmit(onSubmitCallback)}>
        <Grid>
          <FormGrid>
            <FormInput
              disabled={hasSubmitted}
              errors={errors}
              name="firstname"
              id="form-fname"
              inputTitle="First Name"
              handler={register({ required: "This field is required (*)" })}
            ></FormInput>
            <FormInput
              disabled={hasSubmitted}
              errors={errors}
              name="lastname"
              id="form-lname"
              inputTitle="Last Name"
              handler={register({ required: "This field is required (*)" })}
            ></FormInput>
            <FormInput
              colSpan={2}
              disabled={hasSubmitted}
              errors={errors}
              name="email"
              id="form-email"
              inputTitle="E-Mail"
              handler={register({
                required: "This field is required (*)",
                //email regex from https://emailregex.com/
                //eslint-disable-next-line
                pattern: /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/,
                message: "Enter a valid email address",
              })}
              error={
                errors.email?.type === "pattern"
                  ? "Enter a valid email address"
                  : errors.email?.type === "required"
                  ? errors.email.message
                  : ""
              }
            ></FormInput>
            <FormInput
              disabled={hasSubmitted}
              errors={errors}
              name="company"
              id="form-company"
              inputTitle="Company Name"
              handler={register({ required: "This field is required (*)" })}
            ></FormInput>
            <FormInput
              disabled={hasSubmitted}
              errors={errors}
              name="primary_vertical"
              id="form-vertical"
              inputTitle="Primary Target Vertical"
              as={FormInput.Select}
              handler={register()}
            >
              <option disabled value="DEFAULT">
                Please Select
              </option>

              <option value="Campus">Campus</option>
              <option value="City district">City District</option>
              <option value="Grocery">Grocery</option>
              <option value="Healthcare">Healthcare</option>
              <option value="Office">Office Building</option>
              <option value="Retail">Retail</option>
              <option value="Mall">Shopping Mall</option>
              <option value="Stadium">Stadium</option>
              <option value="Public Safety">Public Safety</option>
              <option value="Theme park">Theme Park</option>
              <option value="Transportation Hub">Transportation Hub</option>
              <option value="Warehouse">Warehouse</option>
              <option value="Other">Other</option>
            </FormInput>

            <FormInput
              disabled={hasSubmitted}
              errors={errors}
              name="message"
              id="form-message"
              inputTitle="Message"
              small={false}
              as={FormInput.Textarea}
              handler={register({ required: "This field is required (*)" })}
            ></FormInput>
            <FormSubmitButton
              type="submit"
              disabled={
                !isValidated || Object.keys(errors).length > 0 || hasSubmitted
              }
            >
              <span>Start The Conversation</span>
            </FormSubmitButton>
          </FormGrid>
          <ValidationContainer>
            {showCaptcha && (
              <ReCAPTCHA
                sitekey="6LcYAakZAAAAAEfW6OEAv4QJNAs9Zg50ccHhSIjL"
                onChange={() => setIsValidated(true)}
                onExpired={() => setIsValidated(false)}
              />
            )}
          </ValidationContainer>
        </Grid>
      </FormContainer>
    </>
  )
}

export default ContactForm
